import { useState } from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useUserSettings } from '../../contexts/UserSettingsContext';
import { hasCompletedOnboarding } from '../onboarding';
import { isPremiumContent, canAccessPremium } from '../../lib/subscriptionService';
import { PaywallModal } from '../paywall/PaywallModal';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { Header } from './Header';
import { PageContainer } from './PageContainer';

interface ProtectedLessonProps {
  children: React.ReactNode;
}

/**
 * Route guard that checks if a lesson is unlocked.
 * Redirects to onboarding if not completed, or to last unlocked lesson if locked.
 */
export function ProtectedLesson({ children }: ProtectedLessonProps) {
  const { lessonId } = useParams<{ lessonId: string }>();
  const { settings, isLoading } = useUserSettings();

  // Show loading while settings load
  if (isLoading) {
    return (
      <>
        <Header title="Loading..." />
        <PageContainer>
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        </PageContainer>
      </>
    );
  }

  // Redirect to onboarding if not completed
  const onboardingComplete = hasCompletedOnboarding();

  if (!onboardingComplete) {
    return <Navigate to="/onboarding" replace />;
  }

  // If onboarding completed but settings failed to parse (e.g. schema
  // validation mismatch), render children rather than creating a redirect
  // loop: /lesson → /onboarding → / (OnboardingGuard bounces back).
  if (!settings) {
    return <>{children}</>;
  }

  // Parse lesson ID (format: "b1-l05" -> book 1, lesson 5)
  if (!lessonId) {
    return <>{children}</>;
  }

  const match = lessonId.match(/^b(\d+)-l(\d+)$/);
  if (!match) {
    return <>{children}</>;
  }

  const book = parseInt(match[1], 10);
  const lesson = parseInt(match[2], 10);

  // Check subscription for premium books (Books 2+)
  if (isPremiumContent(book) && !canAccessPremium()) {
    return <PremiumGate bookNumber={book} />;
  }

  // Check if this lesson is unlocked
  const isUnlocked = settings.isLessonUnlocked(book, lesson);

  if (!isUnlocked) {
    // Find the last unlocked lesson to redirect to
    const nextUnlocked = settings.getNextUnlockedLesson();
    const redirectId = `b${nextUnlocked.book}-l${String(nextUnlocked.lesson).padStart(2, '0')}`;

    // Avoid redirect loop - if we're already at the target, just show the content
    if (nextUnlocked.book === book && nextUnlocked.lesson === lesson) {
      return <>{children}</>;
    }

    return <Navigate to={`/lesson/${redirectId}`} replace />;
  }

  return <>{children}</>;
}

/**
 * Route guard for book-level access.
 * Checks both subscription status and progress-based unlock.
 */
export function ProtectedBook({ children, bookNumber }: { children: React.ReactNode; bookNumber: number }) {
  const { settings, isLoading } = useUserSettings();

  if (isLoading) {
    return (
      <>
        <Header title="Loading..." />
        <PageContainer>
          <div className="flex flex-col items-center justify-center min-h-[400px]">
            <LoadingSpinner size="lg" />
          </div>
        </PageContainer>
      </>
    );
  }

  if (!settings) {
    return <>{children}</>;
  }

  // Check subscription for premium books
  if (isPremiumContent(bookNumber) && !canAccessPremium()) {
    return <PremiumGate bookNumber={bookNumber} />;
  }

  if (!settings.isBookUnlocked(bookNumber)) {
    return <Navigate to="/books" replace />;
  }

  return <>{children}</>;
}

/** Shown when a user tries to access premium content without a subscription */
function PremiumGate({ bookNumber }: { bookNumber: number }) {
  const [showPaywall, setShowPaywall] = useState(true);

  return (
    <>
      <Header title={`Book ${bookNumber}`} showBack />
      <PageContainer>
        <div className="flex flex-col items-center justify-center min-h-[400px] text-center px-4">
          <div className="w-16 h-16 rounded-full bg-[var(--color-primary)]/10 flex items-center justify-center mb-4">
            <svg className="w-8 h-8 text-[var(--color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <h2 className="font-display text-xl font-semibold text-[var(--color-ink)] mb-2">
            Premium Content
          </h2>
          <p className="text-[var(--color-ink-muted)] mb-6 max-w-xs">
            Book {bookNumber} and advanced features are available with a subscription.
          </p>
          <button
            onClick={() => setShowPaywall(true)}
            className="px-6 py-3 bg-[var(--color-primary)] text-white rounded-[var(--radius-md)] font-medium hover:bg-[var(--color-primary-dark)] transition-colors"
          >
            View Plans
          </button>
        </div>
      </PageContainer>
      {showPaywall && (
        <PaywallModal onClose={() => setShowPaywall(false)} bookNumber={bookNumber} />
      )}
    </>
  );
}

export default ProtectedLesson;
